import React, { useEffect, useState, useRef, memo } from "react";
import { motion, AnimatePresence, useInView } from "framer-motion";
import usePageVisibility from "../hooks/usePageVisibility";

const GrowthBarGraph = ({ forceAnimation = false }) => {
  const containerRef = useRef(null);
  const [bars, setBars] = useState([]);
  const updateTimeoutRef = useRef(null);
  // State for dot positions and the connecting path data
  const [dotPositions, setDotPositions] = useState([]);
  const [pathData, setPathData] = useState("");
  // State to track container width
  const [containerWidth, setContainerWidth] = useState(300);

  // Refs to store the colored (growth) bar elements
  const coloredBarRefs = useRef({});
  // A ref to always have the latest bars array
  const barsRef = useRef(bars);
  useEffect(() => {
    barsRef.current = bars;
  }, [bars]);

  // Watch the container's in-view status - add small buffer margin to prevent edge flickering
  const inView = useInView(containerRef, { once: false, margin: "-10% 0px" });

  // Add initialization tracking to prevent duplicate setups
  const isInitializingRef = useRef(false);

  // Track previous force animation value to detect changes
  const prevForceAnimationRef = useRef(forceAnimation);

  // Track container width on mount and resize
  useEffect(() => {
    // Capture the current element reference inside the effect
    const currentElement = containerRef.current;

    const updateContainerWidth = () => {
      if (currentElement) {
        const width = currentElement.offsetWidth;
        setContainerWidth(width);
      }
    };

    // Initial measurement
    updateContainerWidth();

    // Set up resize observer with throttling to prevent excessive updates
    let throttleTimeout = null;
    const throttledUpdateWidth = () => {
      if (!throttleTimeout) {
        throttleTimeout = setTimeout(() => {
          updateContainerWidth();
          throttleTimeout = null;
        }, 100);
      }
    };

    const resizeObserver = new ResizeObserver(throttledUpdateWidth);
    if (currentElement) {
      resizeObserver.observe(currentElement);
    }

    // Clean up
    return () => {
      if (currentElement) {
        resizeObserver.unobserve(currentElement);
      }
      resizeObserver.disconnect();
      if (throttleTimeout) {
        clearTimeout(throttleTimeout);
      }
    };
  }, []);

  // When the component goes out of view, clear all bars.
  // When it comes back into view or forceAnimation changes, reinitialize bars.
  useEffect(() => {
    // Check if forceAnimation changed from false to true
    const forceAnimationChanged =
      forceAnimation && !prevForceAnimationRef.current;

    // Update ref with current value for next comparison
    prevForceAnimationRef.current = forceAnimation;

    // Trigger animation when in view naturally OR forced by parent
    const shouldAnimate = inView || forceAnimation;

    if (!shouldAnimate) {
      // Clear out any leftover bars
      setBars([]);
      // Also clear any pending timeouts
      if (updateTimeoutRef.current) {
        clearTimeout(updateTimeoutRef.current);
      }
      // Reset initialization flag when leaving view
      isInitializingRef.current = false;
    } else if (!isInitializingRef.current || forceAnimationChanged) {
      // Reset if forced animation changed
      if (forceAnimationChanged) {
        isInitializingRef.current = false;
        // Clear existing bars for clean restart
        setBars([]);
        if (updateTimeoutRef.current) {
          clearTimeout(updateTimeoutRef.current);
        }
      }

      // Set flag to prevent duplicate initializations on edge cases
      isInitializingRef.current = true;

      // Short delay to ensure DOM is ready (helps with forced animations)
      setTimeout(() => {
        // Reinitialize the bars when coming into view
        const initialBars = Array.from({ length: 5 }, (_, index) => ({
          id: `initial-${index}-${Date.now()}`,
          height: Math.floor(Math.random() * 15) + 25, // random height between 30 and 45%
          position: index,
          isLeaving: false,
        }));
        setBars(initialBars);
      }, 50);
    }
  }, [inView, forceAnimation]);

  const isPageVisible = usePageVisibility();

  // Update bars only while in view, visible, or when forced
  useEffect(() => {
    if ((!inView && !forceAnimation) || !isPageVisible) return;

    const updateBars = () => {
      if (updateTimeoutRef.current) clearTimeout(updateTimeoutRef.current);

      setBars((currentBars) => {
        if (currentBars.length !== 5) return currentBars;
        return currentBars.map((bar, index) =>
          index === 0 ? { ...bar, isLeaving: true } : bar
        );
      });

      updateTimeoutRef.current = setTimeout(() => {
        setBars((current) => {
          if (current.length !== 5) return current;
          const newBars = current
            .filter((bar) => !bar.isLeaving)
            .map((bar, index) => ({
              ...bar,
              position: index,
            }));

          // New bars always have a minimum height of 60%
          const lastHeight = newBars[newBars.length - 1]?.height || 60;
          const minHeight = Math.max(60, lastHeight - 8);
          const maxHeight = Math.min(92, lastHeight + 8);
          const newHeight =
            Math.floor(Math.random() * (maxHeight - minHeight + 1)) + minHeight;

          newBars.push({
            id: `bar-${Date.now()}`,
            height: newHeight,
            position: 4,
            isLeaving: false,
          });

          return newBars;
        });
      }, 300);
    };

    const intervalId = setInterval(updateBars, 1500);
    return () => {
      clearInterval(intervalId);
      if (updateTimeoutRef.current) clearTimeout(updateTimeoutRef.current);
    };
  }, [inView, forceAnimation, isPageVisible]);

  // Responsive calculations based on container width
  const isMobile = containerWidth <= 250;
  const barWidth = isMobile ? 28 : 32; // 28px on mobile, 32px on desktop
  const barSpacing = isMobile ? 52 : 64; // Smaller spacing on mobile

  // Dynamic baseX calculation
  const baseX = (containerWidth - barSpacing * 4 - barWidth) / 2;

  // Ref to track previous dot and path values to avoid dependency cycles
  const prevValuesRef = useRef({
    dotPositions: [],
    pathData: "",
  });

  // Animation loop to update the dot positions and connecting path
  useEffect(() => {
    // Only run when in view or forced, AND when tab is visible
    if ((!inView && !forceAnimation) || !isPageVisible) return;

    let animationFrameId;

    const updateSvgOverlay = () => {
      if (!containerRef.current) {
        animationFrameId = requestAnimationFrame(updateSvgOverlay);
        return;
      }

      const containerRect = containerRef.current.getBoundingClientRect();
      const newDots = [];
      // Only add dots for bars with height >= 50%
      barsRef.current.forEach((bar) => {
        if (bar.height >= 50) {
          const refEl = coloredBarRefs.current[bar.id];
          if (refEl) {
            const dotRect = refEl.getBoundingClientRect();
            // Calculate dot position relative to container and shift up 20px:
            const x = dotRect.left - containerRect.left + dotRect.width / 2;
            const y = dotRect.top - containerRect.top - 20;
            newDots.push({ x, y });
          }
        }
      });
      // Sort dots by x so that the connecting line goes left-to-right.
      newDots.sort((a, b) => a.x - b.x);

      let newPath = "";
      if (newDots.length > 0) {
        newPath = `M ${newDots[0].x} ${newDots[0].y}`;
        for (let i = 1; i < newDots.length; i++) {
          newPath += ` L ${newDots[i].x} ${newDots[i].y}`;
        }
      }

      // Update values in each frame for smooth animation like original
      setDotPositions(newDots);
      setPathData(newPath);

      // Store current values for next comparison
      prevValuesRef.current = {
        dotPositions: newDots,
        pathData: newPath,
      };

      animationFrameId = requestAnimationFrame(updateSvgOverlay);
    };

    animationFrameId = requestAnimationFrame(updateSvgOverlay);

    return () => {
      if (animationFrameId) {
        cancelAnimationFrame(animationFrameId);
      }
    };
  }, [inView, forceAnimation, containerWidth, isPageVisible]);

  return (
    <div
      ref={containerRef}
      className="w-full h-full aspect-square max-w-[300px]"
    >
      <h3 className="text-xl font-bold text-[#ffffff] mb-2 text-center w-full">
        Infinite Growth
      </h3>

      <div className="w-full h-[calc(100%-2rem)] relative">
        {/* Dashed baseline and "Neuralward" label appear after the bars animate */}
        {bars.some((bar) => bar.height >= 50) && (
          <motion.div
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.5, ease: "easeOut", delay: 0.5 }}
            style={{
              position: "absolute",
              bottom: "30%",
              left: 0,
              right: 0,
              zIndex: 3,
              pointerEvents: "none",
            }}
          >
            <div style={{ borderTop: "2px dashed #f062a4" }}></div>
            <div className="flex justify-center mt-2">
              <span
                style={{ backgroundColor: "#f062a4" }}
                className="px-3 py-1 text-[black] text-base rounded-full"
              >
                Neuralward
              </span>
            </div>
          </motion.div>
        )}

        <AnimatePresence mode="popLayout">
          {bars.map((bar, index) => (
            <motion.div
              key={bar.id}
              initial={{ x: 400 }}
              animate={{
                x: bar.isLeaving ? baseX : baseX + bar.position * barSpacing,
              }}
              exit={{ x: bar.position === 0 ? baseX : -barWidth }}
              className="absolute h-full"
              style={{
                left: 0,
                zIndex: bar.isLeaving ? 0 : 1,
                width: `${barWidth}px`,
              }}
            >
              <div className="absolute inset-0 bg-[#282829] rounded-lg">
                {/* The colored bar: initial bars (height below 50) use #402e7f, otherwise #ea3d95 */}
                <motion.div
                  ref={(el) => {
                    if (el) {
                      coloredBarRefs.current[bar.id] = el;
                    } else {
                      delete coloredBarRefs.current[bar.id];
                    }
                  }}
                  initial={{ height: 0 }}
                  animate={{ height: `${bar.height}%` }}
                  transition={{
                    duration: 1,
                    ease: "easeOut",
                    delay: bar.id.startsWith("initial-") ? index * 0.1 : 0,
                  }}
                  className="absolute bottom-0 w-full rounded-t-lg"
                  style={{
                    backgroundColor: bar.height < 50 ? "#402e7f" : "#ea3d95",
                  }}
                />
              </div>
            </motion.div>
          ))}
        </AnimatePresence>

        {/* SVG overlay for the dynamic connecting line and dots */}
        <svg
          className="absolute top-0 left-0 w-full h-full pointer-events-none"
          style={{ overflow: "visible", zIndex: 2 }}
        >
          <path
            d={pathData}
            stroke="dodgerblue"
            strokeWidth={isMobile ? 5 : 6}
            fill="none"
          />
          {dotPositions.map((dot, idx) => (
            <circle
              key={idx}
              cx={dot.x}
              cy={dot.y}
              r={isMobile ? 6 : 8}
              fill="dodgerblue"
            />
          ))}
        </svg>
      </div>
    </div>
  );
};

// Use memo to prevent unnecessary rerenders from parent
export default memo(GrowthBarGraph);
